import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { Plus, Edit, Trash2, Newspaper, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import ImageUploader from "@/components/ImageUploader";
import { EntityPicker } from "@/components/EntityPicker";
import { formatMatchDate } from "@/lib/dates";

const LINK_FIELDS = ["related_team_ids", "related_player_ids", "related_match_ids", "related_competition_ids"];

// ISO (UTC) -> value for <input type="datetime-local"> in the admin's local time
const toLocalInput = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const emptyForm = () => ({
  title: "", content: "", excerpt: "", image_url: "", category: "news", tags: "",
  author: "ABFA", published: true, published_at: toLocalInput(),
  related_team_ids: [], related_player_ids: [], related_match_ids: [], related_competition_ids: [],
});

const STATUS_STYLES = {
  published: "bg-[#009E60] text-white",
  scheduled: "bg-blue-600 text-white",
  draft: "bg-gray-300 text-gray-700",
};

const CATEGORY_LABELS = { news: "News", match_report: "Match Report", press_release: "Press Release" };

const useLinkOptions = () => {
  const [raw, setRaw] = useState({ teams: [], players: [], matches: [], comps: [] });
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/teams`),
      axios.get(`${API}/players`),
      axios.get(`${API}/matches?limit=2000`),
      axios.get(`${API}/competitions`),
    ]).then(([t, p, m, c]) => setRaw({ teams: t.data, players: p.data, matches: m.data, comps: c.data }))
      .catch(() => toast.error("Could not load link options"));
  }, []);
  return useMemo(() => {
    const tName = Object.fromEntries(raw.teams.map(t => [t.team_id, t.name]));
    return {
      related_team_ids: raw.teams.map(t => ({ id: t.team_id, label: t.name })),
      related_player_ids: raw.players.map(p => ({ id: p.player_id, label: `${p.name}${p.position ? ` (${p.position})` : ""}` })),
      related_match_ids: raw.matches.map(m => ({
        id: m.match_id,
        label: `${tName[m.home_team_id] || "?"} vs ${tName[m.away_team_id] || "?"} · ${formatMatchDate(m.match_date, { month: "short", day: "numeric", year: "numeric" })}`,
      })),
      related_competition_ids: raw.comps.map(c => ({ id: c.competition_id, label: `${c.name} ${c.season || ""}`.trim() })),
    };
  }, [raw]);
};

const AdminNews = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const linkOptions = useLinkOptions();

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/admin/news`, { withCredentials: true });
      setNews(response.data);
    } catch (error) {
      console.error("Error fetching news:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchNews(); }, []);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        published_at: formData.published_at ? new Date(formData.published_at).toISOString() : null,
      };
      if (editingArticle) {
        await axios.put(`${API}/news/${editingArticle.article_id}`, payload, { withCredentials: true });
        toast.success("Article updated successfully");
      } else {
        await axios.post(`${API}/news`, payload, { withCredentials: true });
        toast.success("Article created successfully");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchNews();
    } catch (error) {
      console.error("Error saving article:", error);
      toast.error(error.response?.data?.detail || "Failed to save article");
    }
  };

  const handleEdit = (article) => {
    setEditingArticle(article);
    setFormData({
      title: article.title || "",
      content: article.content || "",
      excerpt: article.excerpt || "",
      image_url: article.image_url || "",
      category: article.category || "news",
      tags: (article.tags || []).join(', '),
      author: article.author || "ABFA",
      published: article.published !== false,
      published_at: toLocalInput(article.published_at),
      ...Object.fromEntries(LINK_FIELDS.map(f => [f, article[f] || []])),
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (articleId) => {
    if (!window.confirm("Are you sure you want to delete this article?")) return;
    try {
      await axios.delete(`${API}/news/${articleId}`, { withCredentials: true });
      toast.success("Article deleted successfully");
      fetchNews();
    } catch (error) {
      console.error("Error deleting article:", error);
      toast.error("Failed to delete article");
    }
  };

  const resetForm = () => { setEditingArticle(null); setFormData(emptyForm()); };

  const formatDate = (iso) => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });

  const isFuture = formData.published_at && new Date(formData.published_at) > new Date();
  const isPast = formData.published_at && new Date(formData.published_at) < new Date(Date.now() - 60000);

  return (
    <AdminLayout title="News">
      <div data-testid="admin-news">
        <div className="flex items-center justify-between mb-8">
          <p className="text-gray-500">Manage news articles and press releases</p>
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#FFC72C] hover:bg-[#E6B325] text-black rounded-none" data-testid="add-news-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Article
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading font-bold uppercase">
                  {editingArticle ? "Edit Article" : "Add New Article"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="news-form">
                <div>
                  <label className="text-sm font-medium">Title *</label>
                  <Input name="title" value={formData.title} onChange={handleChange} required className="rounded-none" data-testid="news-title-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Category</label>
                    <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                      <SelectTrigger className="rounded-none" data-testid="news-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="news">News</SelectItem>
                        <SelectItem value="match_report">Match Report</SelectItem>
                        <SelectItem value="press_release">Press Release</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Author</label>
                    <Input name="author" value={formData.author} onChange={handleChange} className="rounded-none" data-testid="news-author-input" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Publication date & time</label>
                  <Input type="datetime-local" name="published_at" value={formData.published_at} onChange={handleChange} required className="rounded-none" data-testid="news-published-at-input" />
                  <p className="text-xs mt-1 text-gray-500" data-testid="news-published-at-hint">
                    {isFuture ? "Scheduled — the article will go live automatically at this time."
                      : isPast ? "Backdated — the article will appear in the archive at this date."
                      : "Publishes now."}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium">Excerpt</label>
                  <Textarea name="excerpt" value={formData.excerpt} onChange={handleChange} className="rounded-none" rows={2} placeholder="Brief summary..." data-testid="news-excerpt-input" />
                </div>
                <div>
                  <label className="text-sm font-medium">Content *</label>
                  <Textarea name="content" value={formData.content} onChange={handleChange} required className="rounded-none" rows={8} data-testid="news-content-input" />
                </div>
                <ImageUploader value={formData.image_url} onChange={(url) => setFormData({ ...formData, image_url: url })} category="article-image" label="Featured Image" previewShape="wide" testId="news-image" />
                <div className="border border-gray-200 p-4 space-y-3 bg-gray-50" data-testid="news-links-section">
                  <p className="text-sm font-heading font-bold uppercase flex items-center gap-2"><Link2 className="w-4 h-4" /> Link this story to</p>
                  <EntityPicker label="Teams" options={linkOptions.related_team_ids} value={formData.related_team_ids} onChange={(v) => setFormData({ ...formData, related_team_ids: v })} testId="news-link-teams" />
                  <EntityPicker label="Players" options={linkOptions.related_player_ids} value={formData.related_player_ids} onChange={(v) => setFormData({ ...formData, related_player_ids: v })} testId="news-link-players" />
                  <EntityPicker label="Matches" options={linkOptions.related_match_ids} value={formData.related_match_ids} onChange={(v) => setFormData({ ...formData, related_match_ids: v })} testId="news-link-matches" />
                  <EntityPicker label="Competitions" options={linkOptions.related_competition_ids} value={formData.related_competition_ids} onChange={(v) => setFormData({ ...formData, related_competition_ids: v })} testId="news-link-competitions" />
                </div>
                <div>
                  <label className="text-sm font-medium">Tags (comma separated)</label>
                  <Input name="tags" value={formData.tags} onChange={handleChange} className="rounded-none" placeholder="football, premier league, results" data-testid="news-tags-input" />
                </div>
                <div className="flex items-center gap-4">
                  <Switch checked={formData.published} onCheckedChange={(checked) => setFormData({...formData, published: checked})} data-testid="news-published-switch" />
                  <label className="text-sm font-medium">{formData.published ? "Published" : "Draft (hidden from the public)"}</label>
                </div>
                <div className="flex gap-4">
                  <Button type="submit" className="flex-1 bg-[#FFC72C] hover:bg-[#E6B325] text-black rounded-none" data-testid="news-submit-btn">
                    {editingArticle ? "Update Article" : "Create Article"}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-none">Cancel</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="space-y-4">{[1,2,3,4].map(i => <div key={i} className="h-24 bg-gray-100 animate-pulse" />)}</div>
        ) : news.length > 0 ? (
          <div className="space-y-4">
            {news.map(article => {
              const linkCount = LINK_FIELDS.reduce((n, f) => n + (article[f]?.length || 0), 0);
              return (
                <div key={article.article_id} className="bg-white border border-gray-200 p-4 flex items-center gap-4 group" data-testid={`admin-news-card-${article.article_id}`}>
                  {article.image_url ? (
                    <img src={article.image_url} alt={article.title} className="w-24 h-24 object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-24 h-24 bg-gray-100 flex items-center justify-center flex-shrink-0"><Newspaper className="w-8 h-8 text-gray-300" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-2 py-0.5 text-xs font-bold uppercase bg-gray-900 text-white">{CATEGORY_LABELS[article.category] || article.category}</span>
                      <span className={`px-2 py-0.5 text-xs font-bold uppercase ${STATUS_STYLES[article.status] || STATUS_STYLES.draft}`} data-testid={`news-status-${article.article_id}`}>{article.status}</span>
                      {linkCount > 0 && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-[#FFC72C]/30 text-black flex items-center gap-1" data-testid={`news-links-count-${article.article_id}`}>
                          <Link2 className="w-3 h-3" /> {linkCount} linked
                        </span>
                      )}
                    </div>
                    <h3 className="font-heading font-bold text-lg uppercase text-[#09090b] truncate">{article.title}</h3>
                    <p className="text-sm text-gray-500" data-testid={`news-date-${article.article_id}`}>{formatDate(article.published_at)} • {article.author}</p>
                  </div>
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button size="sm" variant="outline" onClick={() => handleEdit(article)} className="rounded-none" data-testid={`edit-news-${article.article_id}`}><Edit className="w-4 h-4" /></Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(article.article_id)} className="rounded-none" data-testid={`delete-news-${article.article_id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-20 bg-white border border-gray-200">
            <Newspaper className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="font-heading font-bold text-xl text-gray-400 uppercase mb-2">No News Yet</h3>
            <p className="text-gray-500 mb-4">Add your first article to get started.</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminNews;
